"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewDerivateGrid = void 0;
const immer_1 = __importDefault(require("immer"));
const field_1 = require("../../model/field");
const view_filter_derivate_1 = require("./slice/view_filter_derivate");
const view_group_derivate_1 = require("./slice/view_group_derivate");
const interfaces_1 = require("../../exports/store/interfaces");
const datasheet_1 = require("../../modules/database/store/selectors/resource/datasheet");
const constants_1 = require("../../modules/shared/store/constants");
const model_1 = require("../../model");
const view_derivate_base_1 = require("./view_derivate_base");
class ViewDerivateGrid extends view_derivate_base_1.ViewDerivateBase {
    constructor(state, datasheetId) {
        super(state, datasheetId);
        this.state = state;
        this.datasheetId = datasheetId;
        this.viewFilterDerivate = new view_filter_derivate_1.ViewFilterDerivate(state, datasheetId);
        this.viewGroupDerivate = new view_group_derivate_1.ViewGroupDerivate(state, datasheetId);
    }
    // including the visibleRows of the pre-sorted results, the original getVisibleRowsInner.
    getVisibleRowsWithLazySort(visibleRows, recordMoveType, activeRowInfo) {
        const snapshot = this.state.datasheetMap[this.datasheetId].datasheet.snapshot;
        const recordMap = snapshot.recordMap;
        if (!visibleRows) {
            return [];
        }
        if (!activeRowInfo) {
            return visibleRows;
        }
        const { positionInfo: { recordId, visibleRowIndex } } = activeRowInfo;
        const nextVisibleRows = (0, immer_1.default)(visibleRows, draftVisibleRows => {
            if ([constants_1.RecordMoveType.OutOfView, constants_1.RecordMoveType.WillMove].includes(recordMoveType)) {
                if (constants_1.RecordMoveType.WillMove === recordMoveType) {
                    const nextVisibleRowIndex = draftVisibleRows.findIndex(row => row.recordId === recordId);
                    draftVisibleRows.splice(nextVisibleRowIndex, 1);
                }
                // Insert it only when the record still exists.
                if (recordId in recordMap) {
                    draftVisibleRows.splice(visibleRowIndex, 0, { recordId });
                }
            }
            return draftVisibleRows;
        });
        return nextVisibleRows;
    }
    getRecordMoveType(view, visibleRowsIndexMap, activeRecordId, activeRowInfo) {
        var _a, _b;
        const datasheetId = this.datasheetId;
        const snapshot = (_a = this.state.datasheetMap[this.datasheetId]) === null || _a === void 0 ? void 0 : _a.datasheet.snapshot;
        // getPureVisibleRows gets the latest visibleRows.
        const NOT_MOVE = constants_1.RecordMoveType.NotMove;
        if (!snapshot || !activeRecordId) {
            return NOT_MOVE;
        }
        if (!activeRowInfo) {
            return NOT_MOVE;
        }
        const { positionInfo, type = constants_1.WhyRecordMoveType.UpdateRecord } = activeRowInfo;
        const nextVisibleRowIndex = visibleRowsIndexMap.get(activeRecordId);
        if (positionInfo.isInit) {
            return NOT_MOVE;
        }
        if (type === constants_1.WhyRecordMoveType.NewRecord) {
            if (!visibleRowsIndexMap.has(activeRecordId)) {
                // does not exist in the current view, 
                // but the recordMap exists, indicating that it is filtered
                if (activeRecordId in snapshot.recordMap) {
                    return constants_1.RecordMoveType.OutOfView;
                }
                // The current view does not exist and the recordMap does not exist, 
                // indicating that it is deleted.
                return NOT_MOVE;
            }
            if (nextVisibleRowIndex !== positionInfo.visibleRowIndex) {
                return constants_1.RecordMoveType.WillMove;
            }
        }
        // Reduce unnecessary calculations by judging ahead of time
        if (nextVisibleRowIndex === positionInfo.visibleRowIndex) {
            return NOT_MOVE;
        }
        const { recordSnapshot } = activeRowInfo;
        const nextRecordSnapshot = (0, datasheet_1.getRecordSnapshot)(this.state, datasheetId, activeRecordId);
        // Records are deleted
        if (!nextRecordSnapshot) {
            return constants_1.RecordMoveType.Deleted;
        }
        const fieldMap = (0, datasheet_1.getFieldMap)(this.state);
        const fieldPermissionMap = (_b = this.state.datasheetMap[this.datasheetId]) === null || _b === void 0 ? void 0 : _b.fieldPermissionMap;
        const groupField = (0, datasheet_1.getGroupFields)(view, fieldMap, fieldPermissionMap);
        const filterInfo = (0, datasheet_1.getActiveViewFilterInfo)(this.state);
        const sortInfo = (0, datasheet_1.getActiveViewSortInfo)(this.state);
        const isSearching = (0, datasheet_1.getIsSearching)(this.state);
        const isRecordEffectPositionCellValueChanged = (recordSnapshot) => {
            // Whether the record is pre-sorted is determined by the group, filter, 
            // and sort fields that have auto-sorting turned on.
            const fieldsWhichMakeRecordMove = groupField.map(field => field.id);
            if (sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.keepSort) {
                sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.rules.forEach(rule => fieldsWhichMakeRecordMove.push(rule.fieldId));
            }
            filterInfo === null || filterInfo === void 0 ? void 0 : filterInfo.conditions.forEach(cond => fieldsWhichMakeRecordMove.push(cond.fieldId));
            const _fieldsWhichMakeRecordMove = [...new Set(fieldsWhichMakeRecordMove)].filter(fieldId => {
                const fieldRole = (0, datasheet_1.getFieldRoleByFieldId)(fieldPermissionMap, fieldId);
                return fieldRole !== interfaces_1.Role.None;
            });
            return _fieldsWhichMakeRecordMove.some(fieldId => {
                var _a, _b;
                /**
                * The recordSnapshot passed in by getCellValue will not work for the formula,
                * which is always up-to-date.
                * So store the value of the calculated field in the old recordSnapshot.
                * Used to handle the case of pre-sorted calculated fields.
                */
                const field = fieldMap[fieldId];
                let cv1 = (_b = (_a = recordSnapshot.recordMap[activeRecordId]) === null || _a === void 0 ? void 0 : _a.data[fieldId]) !== null && _b !== void 0 ? _b : null;
                cv1 = (0, model_1.handleEmptyCellValue)(cv1, field_1.Field.bindContext(field, this.state).basicValueType);
                const cv2 = (0, datasheet_1.getCellValue)(this.state, snapshot, activeRecordId, fieldId);
                return !field_1.Field.bindContext(field, this.state).eq(cv1, cv2);
            });
        };
        // Non-search state, and the specified field data changes before causing pre-sorting.
        if (!isSearching && !isRecordEffectPositionCellValueChanged(recordSnapshot)) {
            return NOT_MOVE;
        }
        if (nextVisibleRowIndex == null && nextRecordSnapshot) {
            return constants_1.RecordMoveType.OutOfView;
        }
        if (nextVisibleRowIndex !== positionInfo.visibleRowIndex) {
            return constants_1.RecordMoveType.WillMove;
        }
        return NOT_MOVE;
    }
    /**
     * Incrementally update view-derived data when pre-sorting behavior changes
     *
     * @returns viewDerivationPatch partial derivation data
     */
    getViewDerivationPatchByLazySort(view, prevViewDerivation, activeRowInfo) {
        const activeRecordId = (0, datasheet_1.getActiveRecordId)(this.state);
        const recordMoveType = this.getRecordMoveType(view, prevViewDerivation.pureVisibleRowsIndexMap, activeRecordId, activeRowInfo);
        const visibleRows = this.getVisibleRowsWithLazySort(prevViewDerivation.pureVisibleRows, recordMoveType, activeRowInfo);
        const { groupBreakpoint, linearRows, pureLinearRows } = this.viewGroupDerivate.getGroupDerivation(view, visibleRows, recordMoveType);
        return {
            visibleRows,
            visibleRowsIndexMap: new Map(visibleRows.map((item, index) => [item.recordId, index])),
            recordMoveType,
            groupBreakpoint,
            linearRows,
            linearRowsIndexMap: new Map(linearRows.map((row, index) => [`${row.type}_${row.recordId}`, index])),
            pureLinearRows,
        };
    }
    getViewDerivation(view) {
        view = view || (0, datasheet_1.getCurrentView)(this.state);
        const { rowsWithoutSearch } = super.getViewDerivation(view);
        return Object.assign({ rowsWithoutSearch }, this.getViewDerivationWithSearch(view, rowsWithoutSearch));
    }
    getViewDerivationWithSearch(view, rowsWithoutSearch) {
        const { visibleRows: rowsWithoutLazyShort, searchResults } = this.getSearchRows(rowsWithoutSearch, view);
        const rowsWithoutLazyShortIndexMap = new Map(rowsWithoutLazyShort.map((item, index) => [item.recordId, index]));
        const activeRecordId = (0, datasheet_1.getActiveRecordId)(this.state);
        const activeRowInfo = (0, datasheet_1.getActiveRowInfo)(this.state);
        const recordMoveType = this.getRecordMoveType(view, rowsWithoutLazyShortIndexMap, activeRecordId, activeRowInfo);
        const visibleRows = this.getVisibleRowsWithLazySort(rowsWithoutLazyShort, recordMoveType, activeRowInfo);
        const { groupBreakpoint, linearRows, pureLinearRows } = this.viewGroupDerivate.getGroupDerivation(view, visibleRows, recordMoveType);
        return {
            // Raw rows of data, grouped without any filtering sorting.
            rowsIndexMap: new Map(view.rows.map((item, index) => [item.recordId, index])),
            // Excluding pre-sorted row data, including filtered sorted grouped search
            pureVisibleRows: rowsWithoutLazyShort,
            // Map of the row-order data after view property calculation
            pureVisibleRowsIndexMap: rowsWithoutLazyShortIndexMap,
            // Visual row data, including filtering sorting grouping search pre-sorting
            visibleRows,
            // A map with recordId as key and order as value
            visibleRowsIndexMap: new Map(visibleRows.map((item, index) => [item.recordId, index])),
            // Pre-sorted or delayed sorted move types
            recordMoveType,
            searchResults,
            // Grouping breakpoint data
            /**
            * groupBreakpoint
            * field1 Grouping Breakpoints 0---------10---------20
            * field2 level Grouping Breakpoints 0--3-5-6--10----15---20
            *
            * field1: [0, 10, 20]
            * field2: [0, 3, 5, 6, 10, 15, 20]
            */
            groupBreakpoint,
            /**
            * Guide the table view to draw the structured data of the table,
            * with the hierarchical structure reflected by depth.
            * [
            *    Blank 0
            *    GroupTab 0
            *      GroupTab 1
            *        GroupTab 2
            *          Record 3
            *        Add 2
            *        Blank 2
            *      Blank 1
            *    Blank 0
            * ]
            */
            linearRows,
            /**
            * [`${row.type}_${row.recordId}`, index]
            */
            linearRowsIndexMap: new Map(linearRows.map((row, index) => [`${row.type}_${row.recordId}`, index])),
            // Gantt Chart view of ui row information
            pureLinearRows,
        };
    }
}
exports.ViewDerivateGrid = ViewDerivateGrid;
//# sourceMappingURL=view_derivate_grid.js.map